import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DateRangePicker from '../../common/DateRangePicker';
import StatCard from '../../common/StatCard';
import { showConfirm, showSuccess, showError, showJsonDetails } from '../../../utils/sweetAlertHelper';
import './AccountStatement.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * AccountStatement - Estado de cuenta completo de todas las plataformas
 */
export default function AccountStatement() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    type: 'ALL',
    platform: '',
    limit: 50,
    skip: 0
  });
  
  useEffect(() => {
    fetchStatement();
  }, [filters]);
  
  const fetchStatement = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('app_token');
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` },
        params: filters
      };
      
      const [statementRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/account/statement`, config),
        axios.get(`${API_BASE_URL}/account/summary`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` },
          params: {
            startDate: filters.startDate,
            endDate: filters.endDate
          }
        })
      ]);
      
      setMovements(statementRes.data.data.movements);
      setPagination(statementRes.data.data.pagination);
      setSummary(summaryRes.data.data);
    } catch (err) {
      console.error('Error fetching statement:', err);
      await showError('No se pudo cargar el estado de cuenta. Por favor, intenta nuevamente.', 'Error al Cargar');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSync = async () => {
    const result = await showConfirm(
      'Se sincronizarán los movimientos de los últimos 30 días. Esto puede tomar unos momentos.',
      '¿Sincronizar movimientos de cuenta?',
      { confirmButtonText: 'Sí, sincronizar' }
    );
    
    if (!result.isConfirmed) {
      return;
    }
    
    try {
      setSyncing(true);
      const token = localStorage.getItem('app_token');
      
      await axios.post(`${API_BASE_URL}/account/sync?daysBack=30`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      });
      
      await showSuccess('Los movimientos se han sincronizado correctamente.', '✅ Sincronización Completada');
      fetchStatement();
    } catch (err) {
      await showError(err.response?.data?.error || err.message, 'Error al Sincronizar');
    } finally {
      setSyncing(false);
    }
  };
  
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, skip: 0 }));
  };
  
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      skip: (newPage - 1) * prev.limit
    }));
  };
  
  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };
  
  const getTypeLabel = (type) => {
    const labels = {
      'SPOT_TRADE': 'Spot Trade',
      'FUTURES_PNL': 'Futures PnL',
      'FUTURES_FUNDING': 'Funding Fee',
      'FUTURES_FEE': 'Trading Fee',
      'FUTURES_TRANSFER': 'Transfer',
      'DEPOSIT': 'Deposit',
      'WITHDRAWAL': 'Withdrawal',
      'SOLANA_TRANSACTION': 'Solana TX'
    };
    return labels[type] || type;
  };
  
  const getTypeIcon = (type) => {
    const icons = {
      'SPOT_TRADE': '🔄',
      'FUTURES_PNL': '💰',
      'FUTURES_FUNDING': '📊',
      'FUTURES_FEE': '💳',
      'DEPOSIT': '⬇️',
      'WITHDRAWAL': '⬆️',
      'SOLANA_TRANSACTION': '🪙'
    };
    return icons[type] || '📝';
  };
  
  const getPlatformBadge = (platform) => {
    const badges = {
      'BINANCE_SPOT': { label: 'Spot', color: '#f7931a' },
      'BINANCE_FUTURES': { label: 'Futures', color: '#3b82f6' },
      'SOLANA': { label: 'Solana', color: '#14f195' }
    };
    return badges[platform] || { label: platform, color: '#6b7280' };
  };
  
  return (
    <div className="account-statement">
      {/* Header */}
      <div className="statement-header">
        <div>
          <h1>Account Statement</h1>
          <p className="subtitle">Complete history from all platforms</p>
        </div>
        
        <button 
          className="btn-sync"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? '⏳ Syncing...' : '🔄 Sync Now'}
        </button>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="summary-section">
          <h3>Summary by Platform</h3>
          <div className="summary-grid">
            {summary.platforms.map(platform => (
              <StatCard
                key={platform._id}
                label={platform._id || 'Unknown'}
                value={platform.count}
                detail={`Movements`}
                icon={platform._id === 'BINANCE_SPOT' ? '🟠' : platform._id === 'BINANCE_FUTURES' ? '🔵' : '🟢'}
              />
            ))}
          </div>
          
          <h3>Balance by Asset</h3>
          <div className="assets-grid">
            {summary.assets.map(asset => (
              <div key={asset._id} className="asset-card">
                <span className="asset-name">{asset._id}</span>
                <span className={`asset-amount ${asset.totalAmount >= 0 ? 'positive' : 'negative'}`}>
                  {asset.totalAmount >= 0 ? '+' : ''}{asset.totalAmount.toFixed(8)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleFilterChange}
            showQuickSelect={true}
          />
        </div>
        
        <div className="filters-row">
          <select 
            value={filters.type}
            onChange={(e) => handleFilterChange({ type: e.target.value })}
            className="filter-select"
          >
            <option value="ALL">All Types</option>
            <option value="SPOT_TRADE">Spot Trades</option>
            <option value="FUTURES_PNL">Futures PnL</option>
            <option value="FUTURES_FUNDING">Funding Fees</option>
            <option value="FUTURES_FEE">Trading Fees</option>
            <option value="DEPOSIT">Deposits</option>
            <option value="WITHDRAWAL">Withdrawals</option>
            <option value="SOLANA_TRANSACTION">Solana TXs</option>
          </select>
          
          <select 
            value={filters.platform}
            onChange={(e) => handleFilterChange({ platform: e.target.value })}
            className="filter-select"
          >
            <option value="">All Platforms</option>
            <option value="BINANCE_SPOT">Binance Spot</option>
            <option value="BINANCE_FUTURES">Binance Futures</option>
            <option value="SOLANA">Solana</option>
          </select>
          
          <select 
            value={filters.limit}
            onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
            className="filter-select"
          >
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>
      
      {/* Movements Table */}
      <div className="movements-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading movements...</p>
          </div>
        ) : movements.length === 0 ? (
          <div className="empty-state">
            <p>No movements found</p>
            <span>Try adjusting your filters or sync your account</span>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="movements-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Platform</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Asset</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map(mov => {
                    const platformBadge = getPlatformBadge(mov.details?.platform);
                    
                    return (
                      <tr key={mov._id}>
                        <td className="date">{formatDate(mov.timestamp)}</td>
                        <td>
                          <span className="type-badge">
                            {getTypeIcon(mov.type)} {getTypeLabel(mov.type)}
                          </span>
                        </td>
                        <td>
                          <span 
                            className="platform-badge" 
                            style={{ background: platformBadge.color }}
                          >
                            {platformBadge.label}
                          </span>
                        </td>
                        <td className="description">{mov.description}</td>
                        <td className={`amount ${mov.amount >= 0 ? 'positive' : 'negative'}`}>
                          {mov.amount >= 0 ? '+' : ''}{mov.amount.toFixed(8)}
                        </td>
                        <td className="asset">{mov.asset}</td>
                        <td>
                          {mov.details && (
                            <button
                              className="btn-details-small"
                              onClick={() => showJsonDetails(mov.details, 'Detalles del Movimiento')}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="pagination-btn"
                >
                  ← Previous
                </button>
                
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages}
                  <span className="pagination-detail">
                    ({pagination.total} total movements)
                  </span>
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="pagination-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
